import { createFileRoute, Link } from "@tanstack/react-router";
import hero from "@/assets/hero.webp";
import { RecognitionCards } from "@/components/RecognitionCards";
import { ReviewsMarquee } from "@/components/ReviewsMarquee";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/Eyebrow";
import { SectionTitle } from "@/components/SectionTitle";
import { KnifeIcon, EyeIcon, PeopleIcon } from "@/components/icons/BrandIcons";
import { useState } from "react";
import { Plus, Minus, Award, Medal, Crown } from "lucide-react";

export const Route = createFileRoute("/chi-siamo")({
  head: () => ({
    meta: [
      { title: "Chi siamo — La Bottega delle Carni da Marco" },
      { name: "description", content: "Decenni di passione per la carne, nel cuore di Campobasso. Storia, valori e team della Bottega delle Carni da Marco." },
      { property: "og:title", content: "Chi siamo — La Bottega delle Carni da Marco" },
      { property: "og:description", content: "La storia di una bottega indipendente di pregiate carni a Campobasso." },
      { property: "og:url", content: "https://labottegadellecarnidamarco.it/chi-siamo" },
    ],
    links: [{ rel: "canonical", href: "https://labottegadellecarnidamarco.it/chi-siamo" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ChiSiamoPage,
});

const VALUES = [
  { icon: KnifeIcon, title: "Tradizione", text: "Le nostre tecniche di frollatura e stagionatura sono le stesse di decenni fa. Perché la qualità non si compromette." },
  { icon: EyeIcon, title: "Trasparenza", text: "Conosciamo ogni allevamento, ogni filiera, ogni passaggio. Sappiamo da dove arriva la carne che vendiamo." },
  { icon: PeopleIcon, title: "Comunità", text: "Serviamo Campobasso e il Molise con la stessa cura con cui serviremmo i nostri figli. Molti sono diventati famiglia." },
];

const QUALIFICHE = [
  {
    icon: Award,
    title: "Maestro Macellaio",
    holder: "Santo Barca",
    issuer: "FEDERCARNI",
    text: "Titolo conferito dalla Federazione Nazionale Macellai a chi si distingue per esperienza, professionalità e trasmissione del mestiere.",
  },
  {
    icon: Medal,
    title: "Maestro del Commercio",
    holder: "Santo Barca",
    issuer: "Confcommercio",
    text: "Onorificenza riservata agli imprenditori commerciali che hanno dedicato la propria vita professionale al servizio della comunità.",
  },
  {
    icon: Crown,
    title: "Aquila di Diamante",
    holder: "Santo Barca",
    issuer: "50 & Più Confcommercio",
    text: "Il più alto riconoscimento dell'associazione, assegnato per oltre 50 anni di attività continuativa nel settore.",
  },
];

const FAQS = [
  {
    q: "Posso fare un ordine personalizzato?",
    a: "Certo. Accettiamo ordini personalizzati in qualsiasi momento — basta chiamarci, scriverci su WhatsApp o passare in bottega. Prepariamo tagli, preparati e cesti su misura per le tue esigenze.",
  },
  {
    q: "Effettuate spedizioni a domicilio?",
    a: "No, al momento non spediamo. Tutti gli ordini si effettuano con ritiro in bottega: questo ci permette di garantire la freschezza e la qualità che ci contraddistingue.",
  },
  {
    q: "Posso ritirare anche la domenica?",
    a: "Sì, la domenica mattina è possibile ritirare ordini personalizzati ma solo su prenotazione. Contattaci entro il sabato per concordare l'orario di ritiro.",
  },
  {
    q: "Siete un reparto del supermercato Tigre?",
    a: "No. Siamo una bottega artigianale completamente indipendente che ha sede all'interno del supermercato Tigre di Via Kennedy. Filiera, ricette, stagionatura e prezzi sono interamente nostri: il Tigre è solo l'edificio che ci ospita.",
  },
  {
    q: "Quali sono gli orari della bottega?",
    a: "Lunedì–Sabato 8:00–13:30 e 16:00–20:00. La domenica siamo chiusi al pubblico, ma garantiamo il ritiro su prenotazione.",
  },
  {
    q: "Come posso prenotare un prodotto?",
    a: "Puoi usare il nostro sistema di prenotazione online dalla pagina Prodotti, chiamarci direttamente o scriverci su WhatsApp. Ti confermeremo data e ora di ritiro.",
  },
];

function FaqItem({ q, a, open, onToggle }: { q: string; a: string; open: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-brand-charcoal/10">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 py-5 text-left group"
        aria-expanded={open}
      >
        <span className="font-display text-lg md:text-xl text-brand-charcoal group-hover:text-brand-red transition-colors">{q}</span>
        <span className="shrink-0 w-9 h-9 rounded-full border border-brand-charcoal/15 flex items-center justify-center text-brand-red mt-0.5">
          {open ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      </button>
      <div
        className="grid transition-all duration-300 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <p className="pb-5 pr-12 text-brand-warm-gray leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

function ChiSiamoPage() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <>
      <section className="bg-brand-cream pt-16 pb-12 border-b border-brand-gold/20">
        <div className="container mx-auto px-4 text-center">
          <Eyebrow>Chi siamo</Eyebrow>
          <h1 className="font-display text-brand-charcoal text-4xl md:text-6xl mt-3">Decenni di passione, in ogni taglio.</h1>
          <p className="text-brand-warm-gray mt-4 max-w-2xl mx-auto font-italic-serif italic text-lg">La storia di una bottega che è cresciuta nel cuore di Campobasso.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 grid md:grid-cols-5 gap-12 items-start">
          <div className="md:col-span-3">
            <Reveal>
              <Eyebrow>La storia</Eyebrow>
              <SectionTitle className="mt-3">Dal banco al ricordo di una tradizione</SectionTitle>
              <div className="mt-6 space-y-4 text-brand-warm-gray leading-[1.85]">
                <p>La nostra attività nasce a Campobasso in via Garibaldi nel 1996, aperta da mio padre <strong>Santo Barca</strong>, macellaio dal 1970. Papà ha ricevuto il titolo di <strong>Maestro Macellaio</strong> conferito da FEDERCARNI e quello di <strong>Maestro del Commercio</strong>, ed è stato insignito dell'<strong>Aquila di Diamante</strong> dall'associazione 50 &amp; Più Confcommercio per oltre 50 anni di attività.</p>
                <p>Nel 2013 la bottega è passata a me, <strong>Marco Barca</strong>. Ho sempre avuto una propensione per la lavorazione delle carni e ho imparato quasi tutto da mio padre e da altri colleghi macellai con cui ho collaborato, per far crescere insieme la cultura delle carni locali e valorizzare la <strong>frollatura</strong> come metodo per ammorbidire ed esaltare il sapore della carne.</p>
                <p>Dopo dieci anni in via Garibaldi, nel 2023 — a seguito di un'attenta analisi del quartiere — abbiamo trasferito la bottega all'interno del <strong>Supermercato Gran Risparmio</strong> in Piazza della Repubblica, guadagnando molta visibilità. La chiusura della scuola adiacente ha però portato a un forte calo di presenze, così a <strong>settembre 2025</strong> abbiamo trovato la nostra nuova casa nel <strong>Supermercato Tigre in via Kennedy 1</strong>, Campobasso, dove siamo oggi.</p>
                <p>In tutti questi anni abbiamo puntato su qualità e pulizia, lavorando carni prevalentemente <strong>molisane e a filiera corta</strong>, per garantire sempre uno standard più alto di quello del territorio. Siamo <strong>macelleria e gastronomia</strong>, facciamo parte della <strong>F.I.M. — Federazione Italiana Macellai</strong> e organizziamo spesso <strong>eventi enogastronomici</strong> con le carni e i salumi che produciamo con metodo tradizionale, cioè <strong>stagionatura al naturale</strong>.</p>
                <p>Siamo famosi per la <strong>frollatura delle carni</strong>, in particolare <strong>Costate e Fiorentine</strong> di alta qualità, vendute sia al dettaglio in bottega sia alla ristorazione locale. Accanto alla carne trattiamo una selezione di <strong>pecorino, caprino e caciocavalli molisani</strong> di diverse stagionature, <strong>crudi di Parma</strong> e prodotti di <strong>alta norcineria</strong>: perché ai nostri clienti vogliamo offrire solo il meglio.</p>
                <p>Nella foto di copertina c'è lo staff al completo: a destra mio padre, al centro mia madre, a sinistra io. Scegliendoci scoprirete che la nostra forza è la <strong>cordialità</strong> e la capacità di accontentare sempre il cliente, grazie a una profonda conoscenza delle carni: sappiamo consigliare il taglio giusto per ogni piatto, il modo e i tempi di cottura. Vi aspettiamo.</p>
                <p>Siamo promotori e produttori di un salume unico e storico: <strong>L'Abatina</strong>. La sua storia risale agli <strong>anni '50</strong> e narra dell'Abate <strong>Don Annibale Ricciuti</strong> e della sua perpetua, che lo confezionava a uso esclusivo dell'Abate. Ha forma schiacciata, lunga circa <strong>50 cm</strong>, e una ricetta che si allontana dalla tradizionale salsiccia campobassana per l'uso di ingredienti inusuali. La <strong>Fondazione Slow Food per la Biodiversità Onlus</strong> l'ha riconosciuta come prodotto da tutelare, inserendola nell'<strong>Arca del Gusto</strong>.</p>
                <p>Nel <strong>2025</strong> L'Abatina è stata iscritta nel registro <strong>De.Co. — Denominazione Comunale</strong> del Comune di Campobasso, insieme alla <strong>Soppressata casereccia campobassana</strong> (salume tipico già prodotto nell'Ottocento come delizia del Regno di Napoli) e all'<strong>acciaio traforato</strong>, artigianato artistico legato alla città. Un marchio che tutela l'autenticità dei prodotti, il legame con il territorio e le tradizioni locali. La notizia è stata rilanciata a livello nazionale dall'<strong>agenzia ANSA</strong>.</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="md:col-span-2 sticky top-28">
            <div className="aspect-[3/4] overflow-hidden rounded-md zoom-img-parent">
              <img src={hero} alt="Marco e il team" className="zoom-img w-full h-full object-cover"/>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream-dark/40 py-20">
        <div className="container mx-auto px-4">
          <Reveal>
            <SectionTitle align="center">Cosa significa essere artigiani della carne</SectionTitle>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i*0.06}>
                <div className="bg-white p-8 rounded-md h-full">
                  <v.icon size={36} className="text-brand-red"/>
                  <h3 className="font-display text-2xl mt-4">{v.title}</h3>
                  <p className="text-brand-warm-gray mt-3 leading-relaxed text-sm">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ReviewsMarquee />

      {/* Qualifiche personali */}
      <section className="py-20 bg-brand-cream">
        <div className="container mx-auto px-4 max-w-5xl">
          <Reveal>
            <div className="text-center mb-12">
              <Eyebrow>Le nostre qualifiche</Eyebrow>
              <SectionTitle align="center" className="mt-3">Titoli e onorificenze della famiglia Barca</SectionTitle>
              <p className="text-brand-warm-gray mt-4 font-italic-serif italic">Riconoscimenti personali guadagnati in oltre mezzo secolo di lavoro dietro il banco.</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {QUALIFICHE.map((q, i) => (
              <Reveal key={q.title} delay={i * 0.06}>
                <div className="bg-white border border-brand-gold/30 rounded-md p-8 h-full flex flex-col">
                  <div className="w-14 h-14 rounded-full bg-brand-red/10 flex items-center justify-center text-brand-red">
                    <q.icon size={26} />
                  </div>
                  <h3 className="font-display text-2xl mt-5 text-brand-charcoal">{q.title}</h3>
                  <p className="text-xs uppercase tracking-[0.14em] text-brand-red mt-1">{q.holder} · {q.issuer}</p>
                  <p className="text-brand-warm-gray mt-3 leading-relaxed text-sm">{q.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <RecognitionCards />

      {/* FAQ */}
      <section className="py-20 bg-brand-cream">
        <div className="container mx-auto px-4 max-w-4xl">
          <Reveal>
            <div className="text-center">
              <Eyebrow>Domande frequenti</Eyebrow>
              <SectionTitle align="center" className="mt-3">Tutto quello che vuoi sapere</SectionTitle>
              <p className="text-brand-warm-gray mt-4 font-italic-serif italic">Ordini, ritiri, orari e la nostra indipendenza dentro il Tigre.</p>
            </div>
          </Reveal>
          <div className="mt-12">
            {FAQS.map((f, i) => (
              <FaqItem
                key={f.q}
                q={f.q}
                a={f.a}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-red text-white py-16 text-center">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-white text-3xl md:text-4xl">Vieni a trovarci in bottega.</h2>
          <p className="text-white/85 mt-3">Via J. F. Kennedy, 1 — Campobasso. Ti aspettiamo.</p>
          <Link to="/contatti" className="mt-6 inline-flex items-center gap-2 bg-white text-brand-red px-6 py-3 rounded-md text-sm uppercase tracking-[0.16em] hover:bg-brand-cream">
            Trova la bottega
          </Link>
        </div>
      </section>
    </>
  );
}
